import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Typography,
} from "@material-ui/core";
import moment from "moment";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as actions from "./actions";

import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { toast } from "react-toastify";
import AvgMovingChart from "./Chart";

import { Close, InfoOutlined, Map } from "@material-ui/icons";
import Tooltip from "../../../common/Tooltip";

import { VesselMapPage } from "../..";

const useStyles = makeStyles({
  root: {
    width: "fit-content",
  },
});

const initialSearchType = {
  type: "",
  saveLabel: "",
  name: "",
  firstLoadPort: "",
  lastDischargePort: "",
  loadQuantity: "",
  createdDate: "",
};

function CustomTooltip({ children, content }) {
  const [isVisible, setIsVisible] = useState(false);
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [position, setPosition] = useState("middle");

  useLayoutEffect(() => {
    if (!isVisible || !wrapperRef.current || !tooltipRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRef.current.offsetHeight;
    const windowHeight = window.innerHeight;

    let top = rect.top + rect.height / 2;
    let pos = "middle";

    if (top - tooltipHeight / 2 < 8) {
      top = rect.bottom - rect.height / 2 - 48;
      pos = "bottom";
    } else if (top + tooltipHeight / 2 > windowHeight - 8) {
      top = rect.top + rect.height / 2 + 48;
      pos = "top";
    }

    setCoords({
      top,
      left: rect.right + 12,
    });
    setPosition(pos);
  }, [isVisible, content]); // depend on content so it recalculates height on first render

  return (
    <div
      ref={wrapperRef}
      className="relative inline-block w-full"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}

      {isVisible && (
        <div
          ref={tooltipRef}
          style={{
            top: coords.top,
            left: coords.left,
            transform:
              position === "middle"
                ? "translate(0, -50%)"
                : position === "top"
                  ? "translate(0, -100%)"
                  : "translate(0, 0)",
          }}
          className={`
            fixed z-[999999] w-[520px] bg-white text-gray-900 rounded-md 
            border-2 border-[#313C4C]/20 p-3 shadow-xl
            before:content-[''] before:absolute before:border-8 before:border-transparent
            before:right-full
            ${position === "middle"
              ? "before:top-1/2 before:-translate-y-1/2 before:border-r-[#313C4C]/20"
              : position === "top"
                ? "before:bottom-2 before:border-r-[#313C4C]/20"
                : "before:top-2 before:border-r-[#313C4C]/20"
            }
          `}
          onMouseEnter={() => setIsVisible(true)}
          onMouseLeave={() => setIsVisible(false)}
        >
          <div
            className={`
              relative bg-white rounded-md
              before:content-[''] before:absolute before:border-8 before:border-transparent
              before:right-full
              ${position === "middle"
                ? "before:top-1/2 before:-translate-y-1/2 before:border-r-white"
                : position === "top"
                  ? "before:bottom-2 before:border-r-white"
                  : "before:top-2 before:border-r-white"
              }
            `}
          >
            {content}
          </div>
        </div>
      )}
    </div>
  );
}

const initialSelectedOptions = [
  {
    _id: "67c6c8d464659e3cccfe33e9",
    indexName: "BDI",
  },
  {
    _id: "67c6c8d464659e3cccfe33ea",
    indexName: "BCI",
  },
  {
    _id: "67c6c8d464659e3cccfe3423",
    indexName: "BHSI",
  },
  {
    _id: "67c6c8d464659e3cccfe33f7",
    indexName: "BPI",
  },
  {
    _id: "67c6c8d464659e3cccfe3405",
    indexName: "BSI _58",
  },
];



function UserDashboard(props) {
  const { setLoader = () => { } } = props;
  const [selectedOptions, setSelectedOptions] = useState(initialSelectedOptions);
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(10, "years").toISOString(),
    endDate: moment().toISOString(),
  });
  const [hideSideBar, setHideSidebar] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [prevFilters, setPrevFilters] = useState({
    selectedOptions: [],
    startDate: null,
    endDate: null,
  });
  const [vesselIndexesData, setVesselIndexesData] = useState([]);
  const [vesselIndexesDetails, setVesselIndexesDetails] = useState([]);
  const [isOpenVesselIndexInfoModel, setisOpenVesselIndexInfoModel] =
    useState(false);
  const [vesselIndexInfo, setVesselIndexInfo] = useState({
    indexName: "",
    indexType: "",
    inputType: "manual",
    access: "onlyme",
    briefDescription: "",
    indexDetails: "",
    vesselDetails: "",
    port: [{}],
  });
  const selectedOptionsRef = useRef(selectedOptions);
  const shouldFetchRef = useRef(false);
  useEffect(() => {
    selectedOptionsRef.current = selectedOptions;
  }, [selectedOptions]);



  // Optimized to use ref values and prevent re-renders
  const toggleVesselIndexSelection = useCallback(({ indexName, _id }) => {
    setSelectedOptions((prevSelected) => {
      let newSelection;
      const isSelected = prevSelected.some(
        (item) => item.indexName === indexName
      );

      if (isSelected) {
        newSelection = prevSelected.filter(
          (item) => item.indexName !== indexName
        );
      } else {
        if (prevSelected.length >= 10) {
          return prevSelected;
        }
        newSelection = [...prevSelected, { indexName, _id }];
      }

      shouldFetchRef.current = true;
      return newSelection;
    });
  }, []);

  const fetchVesselIndexes = useCallback(
    async (force = false) => {
      if (!selectedOptionsRef.current.length) return;

      const filter = {
        selectedOptions: selectedOptionsRef.current.map(o => o.indexName),
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };

      if (!force && JSON.stringify(prevFilters) === JSON.stringify(filter)) return;

      try {
        setLoader(true);
        shouldFetchRef.current = false;

        const res = await actions.getVesselIndexes(filter);

        setVesselIndexesData(res?.data ?? []);
        setPrevFilters(filter);
      } catch (error) {
        toast.error("Failed to fetch vessel indexes");
        console.error(error);
      } finally {
        setLoader(false);
      }
    },
    [dateRange, prevFilters]
  );

  useEffect(() => {
    if (shouldFetchRef.current) {
      fetchVesselIndexes(true);
    }
  }, [fetchVesselIndexes, shouldFetchRef.current]);

  const fetchVesselIndexList = useCallback(async () => {
    try {
      setLoader(true);
      const res = await actions.getIndexList();
      setLoader(false);
      setVesselIndexesDetails(res?.data ?? []);
    } catch (error) {
      setLoader(false);
      toast.error("Failed to fetch vessel indexes list");
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchVesselIndexes(true);
    fetchVesselIndexList();
  }, []);

  const handleDateRangeChange = useCallback(async (newDateRange) => {
    const { startDate, endDate } = newDateRange;
    const formattedStartDate = startDate?.toISOString ? startDate.toISOString() : (startDate ? new Date(startDate).toISOString() : "");
    const formattedEndDate = endDate?.toISOString ? endDate.toISOString() : (endDate ? new Date(endDate).toISOString() : "");
    const newRange = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };
    setDateRange(newRange);

    if (selectedOptionsRef.current.length) {
      const filter = {
        selectedOptions: selectedOptionsRef.current.map((o) => o.indexName),
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      };
      try {
        setLoader(true);
        const res = await actions.getVesselIndexes(filter);
        setVesselIndexesData(res?.data ?? []);
        setPrevFilters(filter);
      } catch (error) {
        toast.error("Failed to fetch vessel indexes");
        console.error(error);
      } finally {
        setLoader(false);
      }
    }
  }, [setLoader]);

  const filteredVesselIndexes = useMemo(() => {
    return vesselIndexesDetails.filter((val) =>
      !val.isOwner &&
      val.indexName?.toLowerCase().includes(searchTerm?.toLowerCase())
    );
  }, [searchTerm, vesselIndexesDetails]);

  const onSetOpenMap = () => {
    if (vesselIndexInfo?._id) {
      window.open(`/vesselIndexMap/${vesselIndexInfo?._id}`);
    }
    setVesselIndexInfo({
      indexName: "",
      indexType: "",
      inputType: "manual",
      access: "onlyme",
      briefDescription: "",
      indexDetails: "",
      vesselDetails: "",
      port: [{}],
    });
    setisOpenVesselIndexInfoModel(false);
  };
  const onSetOpenMapForSelectedIndexes = () => {
    if (selectedOptions.length > 0) {
      const ids = selectedOptions.map((item) => item._id).join(",");
      window.open(`/vesselIndexMap/${ids}`);
    }
    setVesselIndexInfo({
      indexName: "",
      indexType: "",
      inputType: "manual",
      access: "onlyme",
      briefDescription: "",
      indexDetails: "",
      vesselDetails: "",
      port: [{}],
    });
  };

  const handleResetSelectedOptions = () => {
    setSelectedOptions([...initialSelectedOptions]);
    selectedOptionsRef.current = [...initialSelectedOptions];
    fetchVesselIndexes(true);
  };

  return (
    <Grid container className="max-w-[100%] overflow-hidden">
      <Grid item xs={12} className="flex h-full">
        <Box className="fixed top-[50px] h-[calc(100vh-54px)] z-30">
          <div
            className={`relative -left-6 transition-all duration-300 ${hideSideBar ? "w-0 overflow-visible border-0" : "w-[280px]"
              } bg-white rounded-none border border-gray-300 shadow-sm h-full flex flex-col`}
          >

            <button
              onClick={() => setHideSidebar((prev) => !prev)}
              className="absolute -right-6 top-4 w-6 h-9 bg-white border border-l-0 border-gray-300 text-[#313C4C] flex items-center justify-center transition hover:bg-gray-100 z-20 cursor-pointer shadow-sm"
              title={hideSideBar ? "Expand sidebar" : "Collapse sidebar"}
            >
              {hideSideBar ? <FaArrowRight size={13} /> : <FaArrowLeft size={13} />}
            </button>

            {/* Sidebar Content */}
            <div className={`pt-3 pb-2 px-2.5 flex flex-col h-full ${hideSideBar ? "hidden" : "block"}`}>
              <h2 className="text-center font-medium text-[11px] text-slate-800 mt-1 mb-2">
                Select Vessel Indexes ({selectedOptions.length}/10 selected)
              </h2>

              {selectedOptions.length >= 10 && (
                <p className="text-rose-500 text-[10px] text-center mb-1">
                  Maximum selection limit reached (10)
                </p>
              )}

              {/* Search */}
              <div className="mb-1 relative">
                <input
                  type="text"
                  className="w-full px-2 py-1 h-7 border border-gray-300 rounded-none focus:outline-none focus:ring-1 focus:ring-[#313C4C] text-[11px] font-medium placeholder:text-[11px] placeholder:font-medium"
                  placeholder="Search vessel index..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="absolute right-2 top-1/2 -translate-y-1/2 px-1 py-0 text-[11px] text-[#313C4C] hover:text-[#0a2a47] font-medium transition" onClick={() => setSearchTerm('')}>Clear</button>
              </div>

              <div className="mb-1">
                <button className="text-[11px] font-medium px-2 py-0.5 rounded-none border border-[#313C4C] text-[#313C4C] hover:bg-[#e6f7ff] transition" onClick={() => handleResetSelectedOptions()}>Reset Selection</button>
              </div>

              {/* Vessel Index List */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden
                scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 
                space-y-1 w-full flex flex-col scroll-thin pt-1 pr-1 pl-0.5">
                {vesselIndexesDetails?.length ? (
                  filteredVesselIndexes?.map((val, index) => {
                    const isSelected = selectedOptions.some(
                      (option) => option.indexName === val?.indexName
                    );

                    const portsDisplay =
                      Array.isArray(val?.port) && val.port.length > 0
                        ? val.port
                          .filter((p) => p?.name && p?.name !== "NA")
                          .map((p) => (p?.country ? `${p.name}, ${p.country}` : p.name))
                          .join(" • ")
                        : "";

                    const tooltipContent = (
                      <div className="flex flex-col space-y-2 p-2 bg-white text-[11px] font-medium rounded-none">
                        {/* Title */}
                        <div>
                          <p className="text-[11px] font-medium text-[#313C4C]">{val?.fullIndexName || val?.indexName || "—"}</p>
                          <p className="text-[11px] text-gray-500 font-medium">{val?.indexType || "—"}</p>
                        </div>

                        {/* Description */}
                        <div className="flex flex-col space-y-0.5">
                          <p className="text-[11px] font-medium text-gray-800">Description</p>
                          <div className="max-h-[80px] overflow-y-auto scroll-thin bg-gray-50 p-1.5 rounded-none border border-gray-200">
                            <p className="text-[11px] text-gray-700 font-medium whitespace-pre-wrap">
                              {val?.briefDescription || "No brief description"}
                            </p>
                          </div>
                        </div>

                        {/* Details */}
                        <div className="flex flex-col space-y-0.5">
                          <p className="text-[11px] font-medium text-gray-800">Details</p>
                          <div className="max-h-[80px] overflow-y-auto scroll-thin bg-gray-50 p-1.5 rounded-none border border-gray-200">
                            <p className="text-[11px] text-gray-700 font-medium whitespace-pre-wrap">
                              {val?.indexDetails || "No index details"}
                            </p>
                          </div>
                        </div>

                        {/* Vessel Details */}
                        <div className="flex flex-col space-y-0.5">
                          <p className="text-[11px] font-medium text-gray-800">Vessel Details</p>
                          <div className="max-h-[80px] overflow-y-auto scroll-thin bg-gray-50 p-1.5 rounded-none border border-gray-200">
                            <p className="text-[11px] text-gray-700 font-medium whitespace-pre-wrap">
                              {val?.vesselDetails || "No vessel details"}
                            </p>
                          </div>
                        </div>

                        {/* Ports */}
                        {!!portsDisplay && (
                          <div className="flex flex-col space-y-0.5">
                            <p className="text-[11px] font-medium text-gray-800">Ports</p>
                            <div className="flex flex-wrap gap-1">
                              {portsDisplay.split(" • ").map((p, i) => (
                                <span
                                  key={i}
                                  className="text-[11px] font-medium px-1.5 py-0.5 bg-[#e6f7ff] text-[#313C4C] rounded-none border border-[#313C4C]/20"
                                >
                                  {p}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );

                    return (
                      <CustomTooltip key={val._id || index} content={tooltipContent}>
                        <div
                          key={index}
                          className={`relative group rounded-none border px-2 py-1.5 transition 
                              w-full min-w-0 text-[11px] font-medium
                              ${isSelected
                              ? "border-[#313C4C]/40 bg-[#e6f7ff]"
                              : selectedOptions.length >= 10
                                ? "border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed"
                                : "border-gray-300 hover:border-[#313C4C]/40 cursor-pointer"
                            }`}
                          onClick={() => {
                            if (isSelected || selectedOptions.length < 10) {
                              toggleVesselIndexSelection({
                                indexName: val?.indexName,
                                _id: val?._id,
                              });
                            }
                          }}
                        >
                          {/* Header */}
                          <div className="flex justify-between items-start w-full">
                            <div>
                              <p className={`text-[11px] font-medium leading-tight ${isSelected ? "text-[#313C4C]" : "text-gray-800"}`}>
                                {val?.fullIndexName || val?.indexName}
                              </p>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-1">
                              <Tooltip text="View Details" position="left">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setisOpenVesselIndexInfoModel(true);
                                    setVesselIndexInfo(val);
                                  }}
                                  className="text-gray-500 hover:text-[#313C4C]"
                                >
                                  <InfoOutlined style={{ fontSize: 13 }} />
                                </button>
                              </Tooltip>
                            </div>
                          </div>

                          {/* Ports */}
                          {val?.port?.length > 0 &&
                            val?.port?.some((p) => p?.name && p?.name !== "NA") && <Divider className="my-0.5" />}

                          {val?.port?.length > 0 && (
                            <p className="mt-0 text-[10px] font-medium text-gray-600 leading-tight">
                              {val?.port
                                ?.filter((p) => p?.name && p?.name !== "NA")
                                .map((p) => (p?.country ? `${p?.name}, ${p?.country}` : p?.name))
                                .join(" • ")}
                            </p>
                          )}
                        </div>
                      </CustomTooltip>
                    );
                  })
                ) : (
                  <div className="text-center text-gray-500 text-[11px] font-medium">No Vessel Indexes found.</div>
                )}
              </div>
            </div>
          </div>

        </Box>
        <Box style={{ width: hideSideBar ? '100%' : 'calc(100% - 288px)', marginLeft: hideSideBar ? '0' : '288px' }} className="transition-all duration-300 pt-0">
          <div className="max-w-[100%] mx-auto">
            <AvgMovingChart
              data={vesselIndexesData}
              selectedOptions={selectedOptions.map(
                (option) => option.indexName
              )}
              handleSubmit={fetchVesselIndexes}
              onDateRangeChange={handleDateRangeChange}
              date={dateRange}
              setDate={setDateRange}
              onViewRoute={onSetOpenMapForSelectedIndexes}
            />
            <VesselMapPage
              vesselIndexs={selectedOptions.map((item) => item._id).join(",")}
            />
          </div>
        </Box>
      </Grid>
      <Dialog
        className="superadmin-dialog"
        open={isOpenVesselIndexInfoModel}
        onClose={() => setisOpenVesselIndexInfoModel(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: "!rounded-none border border-slate-200 shadow-xl overflow-hidden",
        }}
      >
        <DialogTitle className="!p-0">
          <div className="flex justify-between items-center px-3 py-1.5 bg-[#f8fafc] border-b border-slate-200">
            <span className="text-[14px] font-medium text-slate-900">
              Index Name: {vesselIndexInfo.indexName}
            </span>
            <button
              onClick={() => setisOpenVesselIndexInfoModel(false)}
              className="p-0.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
              title="Close"
              aria-label="Close index details"
            >
              <Close style={{ fontSize: 16 }} />
            </button>
          </div>
        </DialogTitle>
        <DialogContent dividers className="!px-3 !py-3 bg-white scroll-thin">
          <div className="space-y-2">
            {/* Ports Section */}
            {vesselIndexInfo.port?.some(port => port?.lat && port?.lng) && (
              <>
                <div className="flex justify-between items-center mb-1">
                  <p className="text-[11px] font-semibold text-[#313C4C]">Ports</p>
                  <button
                    onClick={onSetOpenMap}
                    className="h-[22px] px-2 text-[10px] font-medium border border-slate-300 text-[#313C4C] bg-white hover:bg-slate-50 rounded-none transition-colors cursor-pointer flex items-center gap-1"
                  >
                    <Map style={{ fontSize: 12 }} />
                    See Route
                  </button>
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {vesselIndexInfo.port
                    .filter(port => port?.country && port?.lat && port?.lng && port?.portLabel)
                    .map(port => (
                      <span
                        key={port?._id}
                        className="text-[11px] font-medium bg-sky-50 text-[#313C4C] border border-[#313C4C]/20 px-1.5 py-0.5 rounded-none"
                      >
                        {port?.portLabel} ({port?.country})
                      </span>
                    ))}
                </div>
                <div className="border-t border-slate-100" />
              </>
            )}

            {/* Other Sections */}
            <div>
              <p className="text-[11px] font-semibold text-[#313C4C] mb-0.5">Brief Description</p>
              <p className="text-[11px] text-slate-600 font-medium leading-relaxed whitespace-pre-wrap">
                {vesselIndexInfo.briefDescription || "N/A"}
              </p>
            </div>
            <div className="border-t border-slate-100" />
            <div>
              <p className="text-[11px] font-semibold text-[#313C4C] mb-0.5">Index Details</p>
              <p className="text-[11px] text-slate-600 font-medium leading-relaxed whitespace-pre-wrap">
                {vesselIndexInfo.indexDetails || "N/A"}
              </p>
            </div>
            <div className="border-t border-slate-100" />
            <div>
              <p className="text-[11px] font-semibold text-[#313C4C] mb-0.5">Vessel Details</p>
              <p className="text-[11px] text-slate-600 font-medium leading-relaxed whitespace-pre-wrap">
                {vesselIndexInfo.vesselDetails || "N/A"}
              </p>
            </div>
          </div>
        </DialogContent>

        <DialogActions className="!px-3 !py-1.5 bg-[#f8fafc] border-t border-slate-200 flex justify-end">
          <button
            onClick={() => setisOpenVesselIndexInfoModel(false)}
            className="h-7 px-3 text-[11px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 rounded-none border-none bg-transparent transition-colors cursor-pointer flex items-center"
          >
            Close
          </button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default UserDashboard;
